import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ClientPortalView from './ClientPortalView';
import axios from 'axios';

const ClientPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [schedule, setSchedule] = useState(null);

  // Fetch portal data
  useEffect(() => {
    const fetchPortalData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/public/portal/${token}`);
        setPortalData(response.data.quote);
        setChecklist(response.data.checklist || getDefaultChecklist());
        setSchedule(response.data.schedule || null);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching portal data:', err);
        setError(err.response?.data?.message || 'Unable to load portal. Please check your link.');
        setLoading(false);
      }
    };

    if (token) {
      fetchPortalData();
    }
  }, [token]);

  // Save checklist to backend
  const saveChecklist = async (updatedChecklist) => {
    try {
      await axios.post(`/api/public/portal/${token}/checklist`, {
        checklist: updatedChecklist
      });
    } catch (err) {
      console.error('Error saving checklist:', err);
    }
  };

  // Handle checklist updates
  const handleChecklistUpdate = (newChecklist) => {
    setChecklist(newChecklist);
    saveChecklist(newChecklist);
  };

  // Default checklist structure
  const getDefaultChecklist = () => {
    return [
      {
        category: 'Important Documents',
        items: [
          { id: 1, text: 'Valid passport (check expiration date)', completed: false },
          { id: 2, text: 'Visa requirements confirmed', completed: false },
          { id: 3, text: 'Travel insurance purchased', completed: false },
          { id: 4, text: 'Copies of important documents made', completed: false },
        ]
      },
      {
        category: 'To-Do Before Departure',
        items: [
          { id: 5, text: 'Notify bank and credit card companies of travel', completed: false },
          { id: 6, text: 'Arrange pet/house sitter', completed: false },
          { id: 7, text: 'Hold mail delivery', completed: false },
          { id: 8, text: 'Check-in for flights 24 hours before', completed: false },
          { id: 9, text: 'Download offline maps and travel apps', completed: false },
          { id: 10, text: 'Confirm hotel reservations', completed: false },
        ]
      },
      {
        category: 'Packing List',
        items: [
          { id: 11, text: 'Medications and prescriptions', completed: false },
          { id: 12, text: 'Phone chargers and adapters', completed: false },
          { id: 13, text: 'Comfortable walking shoes', completed: false },
          { id: 14, text: 'Weather-appropriate clothing', completed: false },
          { id: 15, text: 'Sunscreen and toiletries', completed: false },
          { id: 16, text: 'Camera or phone for photos', completed: false },
        ]
      }
    ];
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--muted)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary)] mx-auto mb-4"></div>
          <p className="text-[var(--muted-foreground)]">Loading your trip portal...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--muted)] p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-[var(--foreground)] mb-2">Portal Not Found</h2>
          <p className="text-[var(--muted-foreground)] mb-6">{error}</p>
          <p className="text-sm text-[var(--muted-foreground)]">
            If you believe this is an error, please contact your travel agent.
          </p>
        </div>
      </div>
    );
  }

  // Success state - render portal
  return (
    <ClientPortalView 
      quote={portalData} 
      checklist={checklist}
      schedule={schedule}
      onChecklistUpdate={handleChecklistUpdate}
    />
  );
};

export default ClientPortal;
